import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, ArrowLeft, Trophy, AlertCircle, CheckCircle, UserCheck, CreditCard } from 'lucide-react';
import api from '../../services/api';
import { teamAPI, registrationAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../contexts/ToastContext';
import { EventWithHackathon } from '../../types/events';
import { useRegistration } from '../../contexts/RegistrationContext';
import { useAuth } from '../../contexts/AuthContext';
import GuestRegistrationForm from '../../components/ui/GuestRegistrationForm';
import CustomQuestionsFieldset from '../../components/ui/CustomQuestionsFieldset';
import { validateCustomFields, type AnswerMap, type AnswerValue } from '../../utils/formFieldValidation';

// Declare Razorpay type
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  skills?: Array<{ name: string; level: string }>;
  githubProfile?: string;
  linkedinProfile?: string;
  portfolio?: string;
}

const EventRegistration: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const { clearRegistrationStatus, registerForEvent, registerForHackathon } = useRegistration();
  // A signed-in member answers the same custom questions a guest does.
  const [memberAnswers, setMemberAnswers] = useState<AnswerMap>({});
  const [memberAnswerErrors, setMemberAnswerErrors] = useState<{ [key: string]: string }>({});
  const { user } = useAuth();
  
  const [event, setEvent] = useState<EventWithHackathon | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [guestRegistrationData, setGuestRegistrationData] = useState<any>(null);

  useEffect(() => {
    if (id) {
      fetchEvent();
      // Only fetch user profile if user is authenticated
      if (user) {
        fetchUserProfile();
      }
    }
  }, [id, user]);

  // Cleanup payment processing state on unmount
  useEffect(() => {
    return () => {
      setPaymentProcessing(false);
    };
  }, []);

  // Prevent page refresh/close during payment processing
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (paymentProcessing) {
        e.preventDefault();
        e.returnValue = 'Payment is in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    if (paymentProcessing) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [paymentProcessing]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/events/${id}`);
      const data = response.data;
      setEvent(data);
      
      // Check if registration is still open
      if (data.registrationDeadline) {
        const now = new Date();
        const deadline = new Date(data.registrationDeadline);
        if (now > deadline) {
          error('Registration for this event has closed');
          navigate(`/events/${id}`);
        }
      }
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to fetch event details');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/profile');
      setUserProfile(response.data.data); // Access the data field from the response
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  const isHackathon = event?.eventType === 'hackathon';
  // Typed as a hackathon *and* carrying a hackathon record. Without the record
  // there is nothing for the hackathon endpoints to work with, and the event's
  // own fee is the one to charge.
  const isTeamHackathon = isHackathon && !!event?.hackathon?._id;

  // Initialize Razorpay
  const initializeRazorpay = () => {
    return new Promise((resolve) => {
      // Check if Razorpay is already loaded
      if (window.Razorpay) {
        console.log('✅ Razorpay already loaded');
        resolve(true);
        return;
      }

      console.log('🔄 Loading Razorpay script...');
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        console.log('✅ Razorpay script loaded successfully');
        resolve(true);
      };
      script.onerror = () => {
        console.error('❌ Failed to load Razorpay script');
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  // Create payment order
  const createPaymentOrder = async () => {
    try {
      console.log('🔄 Creating payment order...');
      const endpoint = isTeamHackathon
        ? `/payments/create-hackathon-order/${event?.hackathon?._id}`
        : `/payments/create-order/${id}`;
      
      console.log('📡 Making request to:', endpoint);
      // Send the event we are registering through so the server enforces the
      // deadline and charges the fee this page actually showed.
      const response = await api.post(endpoint, isHackathon ? { eventId: id } : {});
      console.log('✅ Payment order response:', response.data);
      
      // Check if payment service is not configured
      if (response.data.message && response.data.message.includes('not configured')) {
        throw new Error('Payment service is not configured. Please contact administrator.');
      }
      
      return response.data;
    } catch (err: any) {
      console.error('❌ Payment order creation error:', err);
      console.error('❌ Error response:', err.response?.data);
      throw new Error(err.response?.data?.message || 'Failed to create payment order');
    }
  };

  // Verify payment and complete registration
  const verifyPayment = async (paymentData: any) => {
    try {
      const response = await api.post('/payments/verify', {
        ...paymentData,
        eventId: isTeamHackathon ? null : id,
        hackathonId: isTeamHackathon ? event?.hackathon?._id : null,
        registrationData: {
          lookingForTeam: true,
          notes: 'Paid registration',
          // Paid registrations used to discard these, so an event that charged
          // a fee collected no answers to its own questions.
          ...buildMemberAnswerPayload()
        }
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Payment verification failed');
    }
  };

  // Handle guest registration
  const handleGuestRegistration = async (guestData: any) => {
    try {
      setPaymentProcessing(true);
      console.log('🔄 Processing guest registration...', guestData);
      
      // Register guest user
      const response = await registrationAPI.registerForEventAsGuest(id!, guestData);
      
      setPaymentProcessing(false);
      success('Guest registration completed successfully!');
      // Redirect to registration success page
      navigate(`/registration/success/${response.registration._id}`);
    } catch (err: any) {
      console.error('❌ Guest registration error:', err);
      error(err.response?.data?.message || 'Guest registration failed');
      setPaymentProcessing(false);
    }
  };

  // Handle free registration (no payment required)
  const handleFreeRegistration = async () => {
    try {
      setPaymentProcessing(true);
      const answerErrors = validateCustomFields((event?.guestFormFields || []) as any, memberAnswers);
      if (Object.keys(answerErrors).length > 0) {
        setMemberAnswerErrors(answerErrors);
        setPaymentProcessing(false);
        error('Please complete the required questions before registering.');
        return;
      }

      console.log('🔄 Processing free registration...');
      
      // Use RegistrationContext to register and update cache
      if (event?.eventType === 'hackathon' && event?.hackathon?._id) {
        await registerForHackathon(event.hackathon._id, {
          notes: 'Free registration'
        });
      } else {
        await registerForEvent(id!, {
          notes: 'Free registration',
          ...buildMemberAnswerPayload()
        });
      }

      setPaymentProcessing(false);
      success('Registration completed successfully!');
      navigate(`/events/${id}`);
    } catch (err: any) {
      console.error('❌ Free registration error:', err);
      error(err.response?.data?.message || 'Registration failed');
      setPaymentProcessing(false);
    }
  };

  const clearAnswerError = (fieldName: string) =>
    setMemberAnswerErrors((prev) => {
      const next = { ...prev };
      delete next[`custom_${fieldName}`];
      return next;
    });

  /**
   * Same shape the guest form sends: a readable map for the screens that display
   * answers directly, plus the structured record keyed by permanent question id.
   */
  const buildMemberAnswerPayload = () => {
    const fields = (event?.guestFormFields || []) as any[];
    const customFieldResponses = fields
      .map((field) => ({
        fieldId: field.fieldId || field.fieldName,
        fieldName: field.fieldName,
        label: field.fieldLabel,
        fieldType: field.fieldType,
        value: memberAnswers[field.fieldName] as AnswerValue,
      }))
      .filter((r) => r.value !== undefined && r.value !== '' &&
        !(Array.isArray(r.value) && r.value.length === 0));

    const customFields: { [key: string]: string | boolean } = {};
    for (const r of customFieldResponses) {
      customFields[r.fieldName] = Array.isArray(r.value)
        ? r.value.join(', ')
        : (r.value as string | boolean);
    }

    return customFieldResponses.length > 0 ? { customFields, customFieldResponses } : {};
  };

  // Handle payment process
  const handlePayment = async () => {
    try {
      setPaymentProcessing(true);

      // Checked before any money moves, so a missing answer is not discovered
      // after the payment has gone through.
      const answerErrors = validateCustomFields((event?.guestFormFields || []) as any, memberAnswers);
      if (Object.keys(answerErrors).length > 0) {
        setMemberAnswerErrors(answerErrors);
        setPaymentProcessing(false);
        error('Please complete the required questions before paying.');
        return;
      }

      // Check if event is free
      if (!event?.isPaidEvent || (event?.eventFee || 0) === 0) {
        console.log('🔄 Event is free, processing free registration...');
        await handleFreeRegistration();
        return;
      }
      
      console.log('🔄 Starting payment process...');
      
      // Initialize Razorpay
      const razorpayLoaded = await initializeRazorpay();
      if (!razorpayLoaded) {
        throw new Error('Failed to load Razorpay');
      }

      // Create payment order
      console.log('🔄 Creating payment order...');
      const orderData = await createPaymentOrder();
      console.log('✅ Payment order created:', orderData);
      
      // Configure Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Udyoga Pramoda',
        description: `Registration for ${event?.title}`,
        order_id: orderData.orderId,
        handler: async (response: any) => {
          console.log('✅ Payment successful:', response);
          try {
            // Verify payment on backend
            const verificationResult = await verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature
            });

            if (verificationResult.success) {
              setPaymentProcessing(false);
              
              // Clear the registration cache for this event to force a fresh check
              clearRegistrationStatus(isTeamHackathon ? event?.hackathon?._id! : id!);

              success(verificationResult.message);

              // Land on the confirmation page, which names what was paid for and
              // offers the receipt. Falling back to the event page only when the
              // registration id did not come back.
              const registrationId = verificationResult.registration?._id;
              if (registrationId) {
                navigate(`/registration/success/${registrationId}`);
              } else if (isTeamHackathon) {
                navigate(`/hackathons/${event?.hackathon?._id}`);
              } else {
                navigate(`/events/${id}`);
              }
            } else {
              setPaymentProcessing(false);
              error('Payment verification failed');
            }
          } catch (err: any) {
            console.error('❌ Payment verification error:', err);
            error(err.message || 'Payment verification failed');
            setPaymentProcessing(false);
          }
        },
        prefill: {
          name: `${userProfile?.firstName} ${userProfile?.lastName}`,
          email: userProfile?.email,
          contact: userProfile?.phone || ''
        },
        theme: {
          color: '#3B82F6'
        },
        modal: {
          ondismiss: () => {
            console.log('❌ Payment modal dismissed');
            setPaymentProcessing(false);
          }
        }
      };

      console.log('🔄 Opening Razorpay modal with options:', options);
      
      // Open Razorpay modal
      const razorpay = new window.Razorpay(options);
      razorpay.open();

    } catch (err: any) {
      console.error('❌ Payment process error:', err);
      error(err.message || 'Failed to initiate payment');
      setPaymentProcessing(false);
    }
  };

  // Handle unregister with confirmation
  const handleUnregister = async () => {
    const confirmed = window.confirm(
      'Amount paid will not be reimbursed. Are you sure you want to unregister?'
    );
    
    if (!confirmed) {
      return;
    }

    try {
      setSubmitting(true);
      
      if (isHackathon) {
        const hackathonId = event?.hackathon?._id;
        if (!hackathonId) {
          error('Hackathon ID not found');
          return;
        }
        
        await api.delete(`/registrations/hackathon/${hackathonId}/unregister`, {
          data: { leaveTeam: true }
        });
        
        success('Successfully unregistered from hackathon');
      } else {
        await api.delete(`/registrations/event/${id}/unregister`);
        success('Successfully unregistered from event');
      }
      
      navigate(`/events/${id}`);
    } catch (err: any) {
      error(err.response?.data?.message || 'Failed to unregister');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!event) {
    return null;
  }

  // For authenticated users, we need userProfile
  if (user && !userProfile) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl relative">
      {/* Payment Processing Overlay */}
      {paymentProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">
              Processing Payment...
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Please wait while we process your payment with Razorpay.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-xs text-blue-800">
                ⚠️ Do not close this window or refresh the page during payment processing.
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-8">
        <Link to={`/events/${id}`} className="flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Event
        </Link>
        
        <div className="flex items-center gap-3 mb-4">
          {isHackathon ? (
            <Trophy className="h-8 w-8 text-yellow-500" />
          ) : (
            <Calendar className="h-8 w-8 text-blue-600" />
          )}
          <h1 className="text-3xl font-bold text-gray-900">
            {isHackathon ? 'Join Hackathon' : 'Register for Event'}
          </h1>
        </div>
        <p className="text-gray-600">
          {isHackathon 
            ? `Register for ${event.title} - Team creation will be available after registration`
            : `Complete your registration for ${event.title}`
          }
        </p>
      </div>

      <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 ${paymentProcessing ? 'pointer-events-none opacity-50' : ''}`}>
        {/* Registration Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Registration Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Show different content based on user authentication and event type */}
              {user ? (
                /* Authenticated user registration */
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Registration Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Name:</strong> {userProfile?.firstName} {userProfile?.lastName}</div>
                    <div><strong>Email:</strong> {userProfile?.email}</div>
                    {userProfile?.phone && <div><strong>Phone:</strong> {userProfile.phone}</div>}
                    {userProfile?.skills && userProfile.skills.length > 0 && (
                      <div><strong>Skills:</strong> {userProfile.skills.map(s => s.name).join(', ')}</div>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    This information will be used for your registration. Update your profile if needed.
                  </p>

                  {(event?.guestFormFields || []).length > 0 && (
                    <div className="mt-6 border-t border-gray-200 pt-6">
                      <CustomQuestionsFieldset
                        fields={(event?.guestFormFields || []) as any}
                        answers={memberAnswers}
                        errors={memberAnswerErrors}
                        onChange={(fieldName, value) => {
                          setMemberAnswers((prev) => ({ ...prev, [fieldName]: value }));
                          clearAnswerError(fieldName);
                        }}
                        onToggleOption={(fieldName, option, checked) => {
                          // Applied against the latest state, so two quick ticks
                          // do not overwrite one another.
                          setMemberAnswers((prev) => {
                            const current = Array.isArray(prev[fieldName]) ? (prev[fieldName] as string[]) : [];
                            return {
                              ...prev,
                              [fieldName]: checked
                                ? [...current, option]
                                : current.filter((o) => o !== option),
                            };
                          });
                          clearAnswerError(fieldName);
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : event?.isPaidEvent && (event?.eventFee || 0) > 0 ? (
                /* A paid event cannot be joined as a guest: the payment has to
                   attach to an account. Said here rather than after the form,
                   which is where the refusal used to arrive. */
                <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4">
                  <h3 className="mb-2 font-medium text-amber-900">Please sign in to register</h3>
                  <p className="mb-4 text-sm text-amber-900">
                    This event has a registration fee of ₹{event.eventFee} INR. Payment has to be
                    linked to a Udyoga Pramoda account, so registering as a guest is not possible
                    for paid events.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/login"
                      className="inline-flex items-center justify-center rounded-md bg-orange-600 px-6 py-3 font-medium text-white transition-colors hover:bg-orange-700"
                    >
                      Sign in
                    </Link>
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center rounded-md border border-orange-300 px-6 py-3 font-medium text-orange-800 transition-colors hover:bg-orange-100"
                    >
                      Create an account
                    </Link>
                  </div>
                </div>
              ) : event?.registrationType === 'optional_signup' ? (
                /* Guest registration form */
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Guest Registration</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    You can register for this event without creating a Udyogapramoda account. Please provide your information below.
                  </p>
                  <GuestRegistrationForm
                    eventId={id!}
                    guestFormFields={event.guestFormFields || []}
                    onSubmit={handleGuestRegistration}
                    onCancel={() => navigate(`/events/${id}`)}
                    loading={paymentProcessing}
                  />
                </div>
              ) : (
                /* Not authenticated and event requires signup */
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                  <h3 className="font-medium text-red-900 mb-3">Authentication Required</h3>
                  <p className="text-sm text-red-800">
                    This event requires you to sign in with your Udyogapramoda account to register.
                  </p>
                  <div className="mt-3">
                    <Link
                      to="/login"
                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      Sign In to Register
                    </Link>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Payment Information */}
                {(!event?.isPaidEvent || (event?.eventFee || 0) === 0) ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <h3 className="font-medium text-green-900">Free Registration</h3>
                    </div>
                    <div className="text-sm text-green-800 space-y-2">
                      <p><strong>Registration Fee:</strong> ₹0 INR (Free)</p>
                      <p className="text-xs">• No payment required for this event</p>
                      <p className="text-xs">• Registration will be completed immediately</p>
                      <p className="text-xs">• You will receive confirmation via email</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCard className="h-5 w-5 text-blue-600" />
                      <h3 className="font-medium text-blue-900">Payment Required</h3>
                    </div>
                    <div className="text-sm text-blue-800 space-y-2">
                      <p><strong>Registration Fee:</strong> ₹{event?.eventFee || 0} INR</p>
                      <p><strong>Payment Method:</strong> Razorpay (Credit/Debit Cards, UPI, Net Banking)</p>
                      <p className="text-xs">• Payment is required to complete registration</p>
                      <p className="text-xs">• Amount paid will not be reimbursed upon cancellation</p>
                      <p className="text-xs">• Secure payment processing by Razorpay</p>
                    </div>
                  </div>
                )}

                {/* Unified Registration Flow */}
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {isHackathon ? 'Ready to Register for Hackathon' : 'Ready to Register'}
                  </h3>
                  <p className="text-gray-600">
                    {isHackathon 
                      ? 'Your profile information will be used for registration. You can create or join a team after registration.'
                      : 'Your profile information will be used for registration. No additional information needed.'
                    }
                  </p>
                  
                  {isHackathon && (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <h4 className="font-medium text-blue-900 mb-2">Hackathon Information:</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <p><strong>Team Size:</strong> {event.hackathon?.minTeamSize} - {event.hackathon?.maxTeamSize} members</p>
                        <p><strong>Available Teams:</strong> {event.hackathon?.currentTeams}/{event.hackathon?.maxTeams}</p>
                        <p><strong>Problem Statements:</strong> {event.hackathon?.problemStatements?.length || 0} available</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Terms and Conditions Checkbox - Only for Hackathons */}
                {isHackathon && (
                  <div className="border-t pt-6">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="terms-checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        disabled={paymentProcessing}
                        className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label htmlFor="terms-checkbox" className="text-sm text-gray-700">
                        I have read and agree to the{' '}
                        <Link 
                          to="/ai-hackathon-terms" 
                          target="_blank"
                          className="text-primary hover:text-primary-dark underline font-medium"
                        >
                          AI Hackathon Terms of Service
                        </Link>
                        . I understand the rules, terms, and conditions for participating in this hackathon.
                      </label>
                    </div>
                    {!termsAccepted && (
                      <p className="text-red-600 text-sm mt-2 ml-7">
                        You must accept the terms and conditions to register for this hackathon.
                      </p>
                    )}
                  </div>
                )}

                {/* Action Buttons - Only show for authenticated users */}
                {user && (
                  <div className="flex justify-end space-x-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate(`/events/${id}`)}
                      disabled={paymentProcessing}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handlePayment}
                      disabled={paymentProcessing || submitting || (isHackathon && !termsAccepted)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {paymentProcessing ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span className="ml-2">
                            {(!event?.isPaidEvent || (event?.eventFee || 0) === 0) ? 'Processing Registration...' : 'Processing Payment...'}
                          </span>
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          {(!event?.isPaidEvent || (event?.eventFee || 0) === 0) ? 'Register for Free' : `Pay ₹${event?.eventFee || 0} and Register`}
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Event Summary */}
        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>Event Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{event.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{event.description}</p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  <span>{formatDate(event.startTime)} at {formatTime(event.startTime)}</span>
                </div>
                
                <div className="flex items-center text-sm">
                  <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                  <span>Ends: {formatDate(event.endTime)} at {formatTime(event.endTime)}</span>
                </div>

                {event.location && (
                  <div className="flex items-center text-sm">
                    <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{event.location}</span>
                  </div>
                )}

                {isHackathon && event.hackathon ? (
                  <>
                    <div className="flex items-center text-sm">
                      <Users className="h-4 w-4 mr-2 text-gray-500" />
                      <span>{event.hackathon.currentTeams}/{event.hackathon.maxTeams} teams</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <span>Team Size: {event.hackathon.minTeamSize}-{event.hackathon.maxTeamSize} members</span>
                    </div>
                  </>
                ) : event.maxParticipants && (user?.role === 'admin' || event.showParticipantCount !== false) ? (
                  <div className="flex items-center text-sm">
                    <Users className="h-4 w-4 mr-2 text-gray-500" />
                    <span>{event.currentParticipants || 0}/{event.maxParticipants} participants</span>
                  </div>
                ) : null}

                {event.registrationDeadline && (
                  <div className="flex items-center text-sm">
                    <Clock className="h-4 w-4 mr-2 text-gray-500" />
                    <span>Registration Deadline: {formatDate(event.registrationDeadline)} at {formatTime(event.registrationDeadline)}</span>
                  </div>
                )}
              </div>

              {/* Payment Information */}
              {(!event?.isPaidEvent || (event?.eventFee || 0) === 0) ? (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex items-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <h4 className="font-medium text-green-900">Free Registration</h4>
                  </div>
                  <div className="text-sm text-green-800 space-y-2">
                    <p><strong>Registration fee:</strong> ₹0 INR (Free)</p>
                    <p><strong>No payment required</strong></p>
                    <p className="text-xs">• Instant registration confirmation</p>
                    <p className="text-xs">• No payment processing needed</p>
                  </div>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex items-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    <h4 className="font-medium text-green-900">Secure Payment</h4>
                  </div>
                  <div className="text-sm text-green-800 space-y-2">
                    <p><strong>Registration fee:</strong> ₹{event?.eventFee || 0} INR</p>
                    <p><strong>Secure payment via:</strong> Razorpay</p>
                    <p className="text-xs">• Multiple payment options available</p>
                    <p className="text-xs">• Instant confirmation after payment</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EventRegistration; 